import { useState, type FormEvent } from 'react';

import { Button, Field, Input } from '@/design-system';
import type { PublicTenant } from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { TenantMark } from './TenantMark';

export interface AccessCodeGateProps {
    tenant: PublicTenant | null;
    copy: PublicCopy;
    /** `true` zodra een ingevulde code is afgekeurd. */
    invalid: boolean;
    /** Hoeveel pogingen er na een foute code nog over zijn, als de server dat zegt. */
    attemptsLeft: number | null;
    busy: boolean;
    /** `1` op de publieke pagina, `2` in de admin-preview. */
    headingLevel: 1 | 2;
    onSubmit: (code: string) => void;
}

/**
 * Staat de toegangscode aan, dan vraagt de pagina daar eerst om - in de
 * huisstijl van het bedrijf, met de naam van het bedrijf erboven. De code gaat
 * niet mee in de link; de koper krijgt hem los van het keuringsbedrijf.
 */
export function AccessCodeGate({
    tenant,
    copy,
    invalid,
    attemptsLeft,
    busy,
    headingLevel,
    onSubmit,
}: AccessCodeGateProps) {
    const [code, setCode] = useState('');
    const [touched, setTouched] = useState(false);
    const Heading = headingLevel === 1 ? 'h1' : 'h2';

    const error =
        touched && code.trim() === ''
            ? copy.accessCodeEmpty
            : invalid
              ? [
                    copy.accessCodeInvalid,
                    attemptsLeft === null ? null : copy.accessCodeAttemptsLeft(attemptsLeft),
                ]
                    .filter(Boolean)
                    .join(' ')
              : null;

    function submit(event: FormEvent) {
        event.preventDefault();
        setTouched(true);

        if (code.trim() === '') {
            return;
        }

        onSubmit(code.trim());
    }

    return (
        <form
            onSubmit={submit}
            className="flex flex-col gap-[18px] px-[var(--pr-pad-x)] py-9"
            noValidate
        >
            {tenant ? <TenantMark tenant={tenant} /> : null}

            <div>
                <Heading className="m-0 font-[family-name:var(--font-display)] text-[clamp(22px,4cqi,26px)] leading-[1.15] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                    {copy.accessCodeTitle}
                </Heading>
                <p className="mt-2 mb-0 text-[14px] leading-[1.55] text-[var(--text-tertiary-safe)]">
                    {copy.accessCodeBody}
                </p>
            </div>

            <Field
                label={copy.accessCodeLabel}
                labelCase="caps"
                size="lg"
                error={error ?? undefined}
            >
                <Input
                    value={code}
                    mono
                    autoComplete="one-time-code"
                    autoCapitalize="characters"
                    spellCheck={false}
                    maxLength={16}
                    onChange={(event) => setCode(event.target.value)}
                />
            </Field>

            <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
                {copy.accessCodeSubmit}
            </Button>
        </form>
    );
}
